import { CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';

export interface Command {
  readonly id: string;
  readonly timestamp: number;
  readonly type: CommandType;
  execute(): void;
  undo(): void;
}

export type CommandType = 'editCell' | 'bulkEdit' | 'deleteRow' | 'addRow' | 'paste';

export interface CellEditCommand extends Command {
  readonly type: 'editCell';
  readonly position: Position;
  readonly oldValue: CellValue;
  readonly newValue: CellValue;
}

export interface BulkEditCommand extends Command {
  readonly type: 'bulkEdit';
  readonly changes: ReadonlyArray<{
    readonly position: Position;
    readonly oldValue: CellValue;
    readonly newValue: CellValue;
  }>;
}

export interface DeleteRowCommand extends Command {
  readonly type: 'deleteRow';
}

export interface AddRowCommand extends Command {
  readonly type: 'addRow';
}

export interface PasteCommand extends Command {
  readonly type: 'paste';
  readonly startPosition: Position;
  readonly changes: ReadonlyArray<{
    readonly position: Position;
    readonly oldValue: CellValue;
    readonly newValue: CellValue;
  }>;
}

export interface HistoryStack {
  readonly undoStack: ReadonlyArray<Command>;
  readonly redoStack: ReadonlyArray<Command>;
  readonly maxSize: number;
}

export function createHistoryStack(maxSize: number): HistoryStack {
  return { undoStack: [], redoStack: [], maxSize };
}

export function pushCommand(history: HistoryStack, command: Command): HistoryStack {
  const newUndoStack = [...history.undoStack, command];

  const trimmedUndoStack = trim(newUndoStack, history.maxSize);

  return {
    ...history,
    undoStack: trimmedUndoStack,
    redoStack: [],
  };
}

export function undoCommand(history: HistoryStack): {
  history: HistoryStack;
  command: Command | null;
} {
  if (history.undoStack.length === 0) return { history, command: null };

  const lastCommand = history.undoStack[history.undoStack.length - 1];
  const newUndoStack = history.undoStack.slice(0, -1);
  const newRedoStack = [...history.redoStack, lastCommand];

  return {
    history: {
      ...history,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
    },
    command: lastCommand,
  };
}

export function canUndo(history: HistoryStack): boolean {
  return history.undoStack.length > 0;
}

export function redoCommand(history: HistoryStack): {
  history: HistoryStack;
  command: Command | null;
} {
  if (history.redoStack.length === 0) return { history, command: null };

  const lastCommand = history.redoStack[history.redoStack.length - 1];
  const newRedoStack = history.redoStack.slice(0, -1);
  const newUndoStack = [...history.undoStack, lastCommand];

  return {
    history: {
      ...history,
      undoStack: newUndoStack,
      redoStack: newRedoStack,
    },
    command: lastCommand,
  };
}

export function canRedo(history: HistoryStack): boolean {
  return history.redoStack.length > 0;
}

export function clearHistory(history: HistoryStack): HistoryStack {
  return { ...history, undoStack: [], redoStack: [] };
}

function trim<T>(arr: T[], maxSize: number): T[] {
  return arr.length > maxSize ? arr.slice(-maxSize) : arr;
}
